import { CircleAlert, CreditCard, Loader2, Minus, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { AMOK_PASTE } from "@/lib/amokPaste";
import { trackEvent } from "@/lib/site";

function formatAud(value: number) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(value);
}

export function ShopCart() {
  const {
    cart,
    isOpen,
    loading,
    closeCart,
    updateQuantity,
    removeItem,
    proceedToCheckout,
  } = useCart();
  const [drawerError, setDrawerError] = useState("");
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const background = Array.from(
      document.querySelectorAll<HTMLElement>(
        ".site-header, main > section, .site-footer, .mobile-cta"
      )
    );
    background.forEach(element => {
      element.inert = true;
    });
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeCart();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      background.forEach(element => {
        element.inert = false;
      });
      previousFocusRef.current?.focus();
    };
  }, [closeCart, isOpen]);

  if (!isOpen) return null;
  const item = cart?.items[0] ?? null;

  async function changeCartQuantity(lineId: string, quantity: number) {
    setDrawerError("");
    try {
      await updateQuantity(lineId, quantity);
    } catch {
      setDrawerError("The cart could not be updated. Please try again.");
    }
  }

  async function removeCartItem(lineId: string) {
    setDrawerError("");
    try {
      await removeItem(lineId);
    } catch {
      setDrawerError("The item could not be removed. Please try again.");
    }
  }

  return (
    <>
      <button
        type="button"
        className="shop-cart-backdrop"
        aria-label="Close cart"
        onClick={closeCart}
      />
      <aside
        id="shop-cart-drawer"
        ref={dialogRef}
        className="shop-cart-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shop-cart-title"
        aria-describedby="shop-cart-notice"
      >
        <div className="shop-cart-header">
          <div>
            <span>ANGKOR pantry</span>
            <h2 id="shop-cart-title">Your cart</h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close cart"
            onClick={closeCart}
          >
            <X />
          </button>
        </div>

        {item && cart ? (
          <>
            <div className="shop-cart-item">
              {item.image && (
                <img
                  src={item.image.url}
                  alt={
                    item.image.altText ??
                    `${AMOK_PASTE.title} ${AMOK_PASTE.jarSize}`
                  }
                  width={item.image.width ?? 160}
                  height={item.image.height ?? 200}
                />
              )}
              <div>
                <h3>{item.productTitle}</h3>
                <p>
                  {formatAud(Number(item.unitPrice.amount))} per jar, including
                  GST
                </p>
                <div className="shop-cart-quantity" aria-label="Cart quantity">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    disabled={loading || item.quantity <= 1}
                    onClick={() =>
                      void changeCartQuantity(item.lineId, item.quantity - 1)
                    }
                  >
                    <Minus />
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    disabled={
                      loading || item.quantity >= AMOK_PASTE.maximumQuantity
                    }
                    onClick={() =>
                      void changeCartQuantity(item.lineId, item.quantity + 1)
                    }
                  >
                    <Plus />
                  </button>
                </div>
                <button
                  type="button"
                  className="shop-cart-remove"
                  disabled={loading}
                  onClick={() => void removeCartItem(item.lineId)}
                >
                  Remove
                </button>
              </div>
            </div>

            <div className="shop-cart-total">
              <span>Subtotal</span>
              <strong>{formatAud(Number(cart.subtotal.amount))}</strong>
              <small>
                All jars are {AMOK_PASTE.priceDisplay} each, including GST.
              </small>
            </div>

            <div className="shop-cart-shipping-benchmark">
              <span>Australia-wide shipping</span>
              <p>{AMOK_PASTE.shippingLabel}</p>
              <strong>{AMOK_PASTE.shippingPriceDisplay} per order</strong>
            </div>

            <button
              type="button"
              className="button button-gold shop-checkout-button"
              disabled={loading}
              onClick={() => {
                trackEvent("curry_paste_checkout", {
                  quantity: String(item.quantity),
                });
                proceedToCheckout();
              }}
            >
              {loading ? <Loader2 className="shop-spin" /> : <CreditCard />}
              Continue to secure checkout
            </button>
            {drawerError && (
              <p className="shop-validation" role="alert">
                <CircleAlert /> {drawerError}
              </p>
            )}
          </>
        ) : (
          <p className="shop-cart-empty">Your cart is empty.</p>
        )}

        <p className="shop-cart-notice" id="shop-cart-notice">
          Ships within Australia only. Quantities above{" "}
          {AMOK_PASTE.maximumQuantity} jars are not available through online
          checkout.
        </p>
      </aside>
    </>
  );
}
